import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    assignDriverShift,
    createShiftTemplate,
    getDriverShiftWeek,
    setShiftSeats,
    updateCityDriverShifts,
    updateDriverShift,
    updateShiftTemplate,
} from "services/driverShifts";

export const driverShiftKeys = {
    all: ["driver-shifts"],
    week: (cityId, start) => [...driverShiftKeys.all, "week", cityId, start],
};

/**
 * One city's week of shifts. `enabled` waits for a city, because a super admin
 * starts with none chosen.
 */
export const useDriverShiftWeek = (cityId, start) => {
    const query = useQuery({
        queryKey: driverShiftKeys.week(cityId, start),
        queryFn: () => getDriverShiftWeek({ cityId, start }),
        enabled: Boolean(cityId),
    });
    return {
        week: query.data ?? null,
        loading: query.isLoading && Boolean(cityId),
        fetching: query.isFetching,
        error: query.error,
    };
};

/**
 * Every write of the page refreshes every week in the cache: a change to a
 * shift can touch two weeks (a move), and the list is small.
 */
const useShiftMutation = (mutationFn) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: driverShiftKeys.all }),
    });
};

export const useCreateShiftTemplate = () => useShiftMutation(createShiftTemplate);
export const useUpdateShiftTemplate = () => useShiftMutation(updateShiftTemplate);
export const useSetShiftSeats = () => useShiftMutation(setShiftSeats);
export const useAssignDriverShift = () => useShiftMutation(assignDriverShift);
export const useUpdateDriverShift = () => useShiftMutation(updateDriverShift);
export const useUpdateCityDriverShifts = () => useShiftMutation(updateCityDriverShifts);
